import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import usePrevious from '../../hooks/usePrevious';
import equals from 'ramda/src/equals';
import format from 'date-fns/format';
import { DefaultChartStyles, isDefined, sortAlphabetically } from '../../utils';
import { SensorType, } from '../../types';
import { ComposedChart, CartesianGrid, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceArea, ReferenceDot, } from 'recharts';
import ChartDot from '../ChartDot';
import ChartDateTickFormat from '../ChartDateTickFormat';
import SensorLineChartToolTip from '../SensorLineChartToolTip';
import SensorLineChartLegend from '../SensorLineChartLegend';
import Loader from '../Loader';
import ChartDisplayCheckboxes from '../ChartDisplayCheckboxes';
import ChartCommentDot from '../ChartCommentDot';
import styles from './SensorLineChart.module.css';
import { useGraphTouchEvents } from '../../hooks';
import { useRefArea } from '../../hooks/useChartZoomUtils';
const SensorLineChart = (props) => {
    const { isReport, sensorType, locale, fromDate, toDate, ticks, 
    // refAreaStart,
    // refAreaEnd,
    zoomIn, zoom = (zoomRange) => { }, updateRefAreaStart, updateRefAreaEnd, 
    // startZoom,
    // onTouchStart,
    // onTouchMove,
    // onTouchEnd,
    notifications, isLoading, isMobile = false, chartConfig, thresholdLines, data, ianaTimeZoneCode, onClickDuringCommentMode, newCommentReferenceDot, isCommentModeActive, isNewCommentPopupOpen, comments = [], hideCommentsCheckbox, hideThresholdLinesCheckbox, hideNotificationsCheckbox, hideControlDetailsCheckbox, legendPrimaryLabel, height, checkBoxesClassName, syncId, } = props;
    //** UseState + Local Variables **//
    const [showThresholdLines, setShowThresholdLines] = useState(isMobile);
    const [showNotifications, setShowNotifications] = useState(isMobile);
    const [showComments, setShowComments] = useState(isMobile);
    const [isInitialNotificationsLoadingFinished, setIsInitialNotificationsLoadingFinished,] = useState(false);
    const [isInitialCommentsLoadingFinished, setIsInitialCommentsLoadingFinished,] = useState(false);
    const previousNotifications = usePrevious(notifications);
    const previousComments = usePrevious(comments);
    //we sort sensorType alphabetically to ensure temperature line renders above humidity line
    const sortedSensorTypes = useMemo(() => {
        return sensorType.sort(sortAlphabetically);
    }, [sensorType]);
    const isAmbientChart = useMemo(() => {
        return (sortedSensorTypes.includes(SensorType.HUMIDITY) &&
            sortedSensorTypes.includes(SensorType.TEMPERATURE));
    }, [sortedSensorTypes]);
    const filteredNotificationTypes = useMemo(() => {
        if (!notifications)
            return [];
        return Object.keys(notifications).filter((sensorParamType) => {
            //notifications is object mapped by sensorParamType
            //sensorParamType overlaps with sensorTypes:
            //('TEMPERATURE', 'HUMIDITY', BINARY')
            return sortedSensorTypes.includes(sensorParamType);
        });
    }, [sortedSensorTypes, notifications]);
    const doNotificationsExist = useMemo(() => {
        return filteredNotificationTypes.length > 0;
    }, [filteredNotificationTypes]);
    const doCommentsExist = useMemo(() => {
        return comments.length > 0;
    }, [comments]);
    const yUnits = useMemo(() => {
        if (!chartConfig || !sortedSensorTypes)
            return null;
        //reverse alphabetically sort to ensure temperature yAxis
        //is first before humidity yAxis
        return [...sortedSensorTypes].reverse().map((s) => chartConfig[s].unit);
    }, [chartConfig, sortedSensorTypes]);
    const isAbleToZoom = !isCommentModeActive;
    // refAreaStart and refAreaEnd should not stay at parent component to avoid too many rerender
    const { refAreaRange, setRefAreaFrom, setRefAreaTo, updateZoomRange } = useRefArea(zoom);
    const { from: refAreaStart, to: refAreaEnd } = refAreaRange;
    const { startZoom, onTouchStart, onTouchMove, onTouchEnd } = useGraphTouchEvents(isAbleToZoom, setRefAreaFrom, setRefAreaTo, updateZoomRange, true);
    // TODO: remove props refAreaStart and refAreaEnd
    useEffect(() => {
        if (!isDefined(props.refAreaStart))
            return;
        setRefAreaFrom(Number(props.refAreaStart));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.refAreaStart]);
    useEffect(() => {
        if (!isDefined(props.refAreaEnd))
            return;
        setRefAreaTo(Number(props.refAreaEnd));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.refAreaEnd]);
    //** UseEffect **//
    useEffect(() => {
        if (!equals(previousNotifications, notifications) &&
            doNotificationsExist &&
            !isInitialNotificationsLoadingFinished) {
            setShowNotifications(true);
            setIsInitialNotificationsLoadingFinished(true);
        }
    }, [
        previousNotifications,
        notifications,
        doNotificationsExist,
        isInitialNotificationsLoadingFinished,
    ]);
    useEffect(() => {
        if (!equals(previousComments, comments) &&
            doCommentsExist &&
            !isInitialCommentsLoadingFinished) {
            setShowComments(true);
            setIsInitialCommentsLoadingFinished(true);
        }
    }, [
        previousComments,
        comments,
        doCommentsExist,
        isInitialCommentsLoadingFinished,
    ]);
    useEffect(() => {
        if (isCommentModeActive) {
            setShowNotifications(false);
        }
    }, [isCommentModeActive, doNotificationsExist]);
    //** End UseEffect **//
    const getMaxY = (dataMax) => {
        const maxY = Math.max(...thresholdLines.map((t) => t.value).concat([dataMax]));
        return Math.ceil(maxY);
    };
    const calculateYAxisDomain = (chartType) => {
        if (chartType === 'humidity') {
            return [0, 100];
        }
        if (chartType === 'binary' || chartType === 'open_counter') {
            return [0, 1];
        }
        if (chartType === 'temperature') {
            return ['auto', (dataMax) => getMaxY(dataMax)];
        }
        //this is recharts API's default domain
        return [0, 'auto'];
    };
    const formatYAxisTicks = (tickValue, chartType) => {
        if (chartType === 'Binary') {
            if (tickValue === 0) {
                return 'Closed';
            }
            else if (tickValue === 1) {
                return 'Open';
            }
            else {
                return '';
            }
        }
        return tickValue;
    };
    const onMouseDown = (e) => {
        if (!isAbleToZoom)
            return;
        // TODO: remove props onTouchStart and use useGraphTouchEvent
        if (props.onTouchStart) {
            props.onTouchStart(e);
        }
        else if (e && e.activeLabel && updateRefAreaStart) {
            updateRefAreaStart(e.activeLabel);
        }
        else {
            onTouchStart(e);
        }
    };
    const onMouseMove = (e) => {
        if (!isAbleToZoom)
            return;
        // TODO: remove props onTouchMove and use useGraphTouchEvent
        if (props.onTouchMove) {
            props.onTouchMove(e);
        }
        else if (e && e.activeLabel && refAreaStart && updateRefAreaEnd) {
            updateRefAreaEnd(e.activeLabel);
        }
        else if (e) {
            onTouchMove(e);
        }
    };
    const onMouseUp = () => {
        if (!isAbleToZoom)
            return;
        // TODO: remove props onTouchEnd and use useGraphTouchEvent
        if (props.onTouchEnd) {
            props.onTouchEnd();
        }
        else if (zoomIn) {
            zoomIn();
        }
        else {
            onTouchEnd();
        }
    };
    if (!isLoading && (!chartConfig || sensorType.length === 0)) {
        return _jsx("div", { children: "Something went wrong. Please try again later." }, void 0);
    }
    return (_jsxs("div", Object.assign({ className: styles.root }, { children: [isLoading && (_jsx("div", Object.assign({ className: styles.loading }, { children: _jsx(Loader, {}, void 0) }), void 0)), yUnits && (_jsxs("div", Object.assign({ className: `${isReport
                    ? styles.reportYAxisLabelContainer
                    : styles.yAxisLabelContainer}` }, { children: [_jsx("div", Object.assign({ className: styles.leftYAxisLabel }, { children: yUnits[0] }), void 0), yUnits[1] && (_jsx("div", Object.assign({ className: styles.rightYAxisLabel }, { children: yUnits[1] }), void 0))] }), void 0)), _jsx(ResponsiveContainer, Object.assign({ width: "100%", height: height !== null && height !== void 0 ? height : DefaultChartStyles.chartHeight }, { children: _jsxs(ComposedChart, Object.assign({ syncId: syncId, onClick: onClickDuringCommentMode, onMouseDown: onMouseDown, onMouseMove: onMouseMove, onMouseUp: onMouseUp }, { children: [_jsx(CartesianGrid, { vertical: false, stroke: DefaultChartStyles.axisLineColor }, void 0), _jsx(XAxis, { allowDataOverflow: true, axisLine: false, tickLine: false, dataKey: "timestamp", type: "number", domain: [fromDate, toDate], interval: 0, minTickGap: 0, ticks: ticks, tick: ({ x, y, payload }) => (_jsx(ChartDateTickFormat, { x: x, y: y, payload: Object.assign(Object.assign({}, payload), { fromDate,
                                    toDate }), locale: locale, ianaTimeZoneCode: ianaTimeZoneCode, isReport: isReport }, void 0)) }, void 0), [...sortedSensorTypes].reverse().map((type, index) => {
                            const config = chartConfig && chartConfig[type];
                            if (!config) {
                                return null;
                            }
                            return (_jsx(YAxis, { width: DefaultChartStyles.yAxisWidth, yAxisId: type, domain: calculateYAxisDomain(config.samplesType), axisLine: false, tickLine: false, dataKey: config.samplesType, orientation: index === 0 ? 'left' : 'right', tickFormatter: (tickValue) => formatYAxisTicks(tickValue, config.title), tick: Object.assign(Object.assign({}, DefaultChartStyles.tick), { fontSize: isReport
                                        ? DefaultChartStyles.tick.reportFontSize
                                        : DefaultChartStyles.tick.fontSize }) }, `chartYAxis${config.samplesType}`));
                        }), sortedSensorTypes.map((type, index) => {
                            const config = chartConfig && chartConfig[type];
                            if (!config) {
                                return null;
                            }
                            return (_jsx(Line, { yAxisId: type, formatter: config.tooltipFormatterFn, dot: false, data: isLoading ? [] : data, dataKey: config.samplesType, stroke: isAmbientChart && type === 'HUMIDITY' ? '#626DF9' : '#2ADDD0', isAnimationActive: false, name: `${config.title} ${config.unit ? config.unit : ''}`, unit: config.unit, type: config.type, activeDot: isCommentModeActive }, `chartSamples${type}`));
                        }), chartConfig &&
                            showNotifications &&
                            notifications &&
                            filteredNotificationTypes.map((notificationType) => {
                                return (_jsx(Line, { activeDot: false, yAxisId: notificationType, dataKey: "notificationValue", data: isLoading ? [] : notifications[notificationType], name: `Notification-${notificationType}`, formatter: (v) => v, strokeWidth: 0, legendType: "none", isAnimationActive: false, dot: _jsx(ChartDot, {}, void 0) }, `notificationLine-${notificationType}`));
                            }), (chartConfig === null || chartConfig === void 0 ? void 0 : chartConfig[SensorType.HUMIDITY]) &&
                            showComments &&
                            comments &&
                            comments.length > 0 && (_jsx(Line, { activeDot: false, yAxisId: SensorType.HUMIDITY, dataKey: "commentValue", data: comments.filter((c) => c.valueType === SensorType.HUMIDITY), strokeWidth: 0, legendType: "none", isAnimationActive: false, dot: ({ cx, cy }) => _jsx(ChartCommentDot, { cx: cx, cy: cy }, void 0), formatter: chartConfig[SensorType.HUMIDITY].tooltipFormatterFn }, void 0)), (chartConfig === null || chartConfig === void 0 ? void 0 : chartConfig[SensorType.BINARY]) &&
                            showComments &&
                            comments &&
                            comments.length > 0 && (_jsx(Line, { activeDot: false, yAxisId: SensorType.BINARY, dataKey: "commentValue", data: comments.filter((c) => c.valueType === SensorType.BINARY), strokeWidth: 0, legendType: "none", isAnimationActive: false, dot: ({ cx, cy }) => _jsx(ChartCommentDot, { cx: cx, cy: cy }, void 0), formatter: chartConfig[SensorType.BINARY].tooltipFormatterFn }, void 0)), (chartConfig === null || chartConfig === void 0 ? void 0 : chartConfig[SensorType.TEMPERATURE]) &&
                            showComments &&
                            comments &&
                            comments.length > 0 && (_jsx(Line, { activeDot: false, yAxisId: SensorType.TEMPERATURE, dataKey: "commentValue", data: comments.filter((c) => c.valueType === SensorType.TEMPERATURE), strokeWidth: 0, legendType: "none", isAnimationActive: false, dot: ({ cx, cy }) => _jsx(ChartCommentDot, { cx: cx, cy: cy }, void 0), formatter: chartConfig[SensorType.TEMPERATURE].tooltipFormatterFn }, void 0)), !hideThresholdLinesCheckbox &&
                            showThresholdLines &&
                            thresholdLines.length > 0
                            ? thresholdLines
                                .filter((t) => t.type !== undefined)
                                .map((t) => (_jsx(ReferenceLine, { y: t.value, yAxisId: t.type, stroke: "red", strokeDasharray: "3 3" }, t.title)))
                            : null, !isNewCommentPopupOpen && (_jsx(Tooltip, { content: startZoom ? (_jsx("div", {}, void 0)) : (({ payload, label }) => (_jsx(SensorLineChartToolTip, { locale: locale, payload: payload, label: label, ianaTimeZoneCode: ianaTimeZoneCode }, void 0))), isAnimationActive: false, labelFormatter: (t) => format(t, locale.fullDateTimeFormat) }, void 0)), _jsx(Legend, { content: _jsx(SensorLineChartLegend, { isReport: isReport, showNotifications: !hideNotificationsCheckbox, showComments: !hideCommentsCheckbox, primaryLabel: determineLegendPrimaryLabel(sortedSensorTypes, legendPrimaryLabel, isAmbientChart), secondaryLabel: isAmbientChart ? 'Humidity' : undefined }, void 0), wrapperStyle: DefaultChartStyles.legendContainer }, void 0), refAreaStart && refAreaEnd ? (_jsx(ReferenceArea, { yAxisId: sortedSensorTypes[0], x1: refAreaStart, x2: refAreaEnd, strokeOpacity: 0.3 }, void 0)) : null, newCommentReferenceDot && (_jsx(ReferenceDot, { isFront: true, yAxisId: sortedSensorTypes[0], x: newCommentReferenceDot.x, y: newCommentReferenceDot.y, fill: "#0095ff", stroke: "#0095ff", r: 3 }, void 0))] }), void 0) }), void 0), !isMobile && (_jsx(ChartDisplayCheckboxes, { className: checkBoxesClassName, showComments: showComments, onClickCommentsCheckbox: () => setShowComments(!showComments), isCommentsCheckboxDisabled: !comments || comments.length === 0, hideCommentsCheckbox: hideCommentsCheckbox, showThresholdLines: showThresholdLines, onClickThresholdLinesCheckbox: () => setShowThresholdLines(!showThresholdLines), isThresholdLinesCheckboxDisabled: thresholdLines.length === 0, hideThresholdLinesCheckbox: hideThresholdLinesCheckbox, showNotifications: showNotifications, onClickNotificationsCheckbox: () => setShowNotifications(!showNotifications), isNotificationsCheckboxDisabled: filteredNotificationTypes.length === 0 || isCommentModeActive, hideNotificationsCheckbox: hideNotificationsCheckbox, hideControlDetailsCheckbox: hideControlDetailsCheckbox }, void 0))] }), void 0));
};
const determineLegendPrimaryLabel = (sortedSensorTypes, legendPrimaryLabel, isAmbientChart) => {
    if (sortedSensorTypes && isAmbientChart) {
        return 'Temperature';
    }
    return legendPrimaryLabel || undefined;
};
export default SensorLineChart;
